import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Checkbox_, Confirmation_, Grey_, Main_Plain, Partial_Loader, Slider_ } from 'monica-alexandria';
import { checkPermissions } from '../../../helpers/checkPermissions';
import { get_pending_merge, merge_records, preview_merge_records } from '../../../services/api/Merges';
import Merge_Preview from './Merge_Preview';

const ConsolidateBrandsContent = styled.div`
    display: grid;
    gap: 2rem;
    min-width: 0;
    color: ${p => p.theme.color};
`;

const ConsolidateBrandsCard = styled.div`
    display: grid;
    gap: 1rem;
    padding: var(--normalPads);
    border-radius: var(--smallRadius);
    background: ${p => p.theme.background};

    p, h5, h6 {
        overflow-wrap: anywhere;
    }
`;

const read_brand_label = brand => `${brand?.internalName || brand?.name || brand?.actualBusinessName || brand?._id || 'Unnamed brand'}`.trim();
const read_brand_subtitle = brand => [
    brand?.actualBusinessName && brand.actualBusinessName !== brand.internalName && brand.actualBusinessName !== brand.name ? brand.actualBusinessName : '',
    ...(brand?.emails || []),
    ...(brand?.phones || [])
].filter(Boolean).join(' · ');
const read_brand_record = brand => ({ id: `${brand._id}`, label: read_brand_label(brand), subtitle: read_brand_subtitle(brand) });

export default function Consolidate_Brands(props) {
    const brands = (props.brands || []).filter(brand => brand?._id && !brand.archived).map(read_brand_record);
    const [selectedIds, setSelectedIds] = useState([]);
    const [preview, setPreview] = useState(null);
    const [choices, setChoices] = useState({});
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [checkingPending, setCheckingPending] = useState(true);
    const [pendingChecked, setPendingChecked] = useState(false);
    const [pendingRetry, setPendingRetry] = useState(0);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const requestRef = useRef(0);
    const savingRef = useRef(false);
    const hasPermission = checkPermissions('King');
    const previewSources = preview ? preview.sources || [preview.source] : [];
    const unresolved = preview?.fields.some(field => field.conflict && !choices[field.key]);
    const canConsolidate = hasPermission && preview && !unresolved && !preview.blockers.length && !saving && !loadingPreview;

    useEffect(() => {
        let active = true;
        if (!hasPermission || !brands.length) {
            setCheckingPending(false);
            setPendingChecked(true);
            return () => { active = false; };
        }
        setCheckingPending(true);
        setError('');
        Promise.all(brands.map(brand => get_pending_merge('brand', brand.id))).then(results => {
            if (!active) return;
            const pending = results.find(Boolean);
            if (pending) {
                setPreview(pending);
                setSelectedIds([pending.survivor, ...(pending.sources || [pending.source])].map(record => record.id));
                setChoices(pending.choices || {});
            }
            setPendingChecked(true);
        }).catch(fetchError => {
            if (active) setError(fetchError.response?.data?.message || 'There was an error checking for an unfinished consolidation');
        }).finally(() => {
            if (active) setCheckingPending(false);
        });
        return () => { active = false; };
    }, [hasPermission, pendingRetry, props.leadId]);

    useEffect(() => () => { requestRef.current += 1; }, []);

    const toggle_brand = id => {
        if (savingRef.current || preview?.resume) return;
        setSelectedIds(current => current.includes(id) ? current.filter(selectedId => selectedId !== id) : [...current, id]);
    };

    const load_preview = async (survivorId, sourceIds, message = '') => {
        if (savingRef.current || !hasPermission || preview?.resume) return;
        const requestId = ++requestRef.current;
        setLoadingPreview(true);
        setPreview(null);
        setChoices({});
        setConfirming(false);
        setError(message);
        try {
            const result = await preview_merge_records('brand', survivorId, sourceIds.length === 1 ? sourceIds[0] : sourceIds);
            if (requestId === requestRef.current) setPreview(result);
        } catch (previewError) {
            if (requestId === requestRef.current) setError(previewError.response?.data?.message || 'There was an error preparing the consolidation');
        } finally {
            if (requestId === requestRef.current) setLoadingPreview(false);
        }
    };

    const review_selection = () => {
        if (selectedIds.length < 2) return;
        load_preview(selectedIds[0], selectedIds.slice(1));
    };

    const change_selection = () => {
        if (savingRef.current || preview?.resume) return;
        requestRef.current += 1;
        setPreview(null);
        setChoices({});
        setConfirming(false);
        setError('');
    };

    const close_consolidation = () => {
        if (!savingRef.current) props.onClose();
    };

    const submit_consolidation = async () => {
        if (!canConsolidate || savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setError('');
        try {
            const result = await merge_records(preview, choices);
            props.onMerged(result);
        } catch (mergeError) {
            setConfirming(false);
            const message = mergeError.response?.data?.message || 'There was an error consolidating these brands';
            const preserveReview = ['MERGE_RETRY_REQUIRED', 'MERGE_ALREADY_RUNNING', 'MERGE_LEASE_LOST'].includes(mergeError.response?.data?.code) || !mergeError.response;
            if (mergeError.response?.status === 409 && !preserveReview && !preview.resume) {
                savingRef.current = false;
                setSaving(false);
                await load_preview(preview.survivor.id, previewSources.map(source => source.id), message);
            } else {
                if (preserveReview) setPreview(current => ({ ...current, resume: true }));
                setError(message);
            }
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    let actionText = 'Consolidate brands';
    if (preview?.resume) actionText = 'Resume consolidation';
    if (saving) actionText = 'Consolidating brands';

    return (
        <Slider_
            title={props.t('Consolidate brands')}
            text={props.t(actionText)}
            disabled={!canConsolidate}
            onClick={() => { if (canConsolidate) setConfirming(true); }}
            grey={props.t('Cancel')}
            onGrey={close_consolidation}
            onClose={close_consolidation}
        >
            <ConsolidateBrandsContent>
                {!hasPermission ? <p role="alert">{props.t('Only Kings can consolidate brands.')}</p> : null}
                {checkingPending ? <Partial_Loader text={props.t('Checking for an unfinished consolidation')}></Partial_Loader> : null}
                {hasPermission && pendingChecked && brands.length < 2 ? <p>{props.t('This lead needs at least two saved brands before they can be consolidated.')}</p> : null}
                {hasPermission && pendingChecked && brands.length >= 2 && !preview ? (
                    <ConsolidateBrandsCard>
                        <h5>{props.t('Choose brands to consolidate')}</h5>
                        <p>{props.t('Select at least two brands. The first selected brand is kept unless you choose another survivor in the preview.')}</p>
                        {brands.map(brand => (
                            <div key={brand.id}>
                                <Checkbox_ text={`${brand.label} · ${brand.id}`} checked={selectedIds.includes(brand.id)} onClick={() => toggle_brand(brand.id)}></Checkbox_>
                                {brand.subtitle ? <p>{brand.subtitle}</p> : null}
                            </div>
                        ))}
                        <Main_Plain text={props.t('Review selected brands')} disabled={selectedIds.length < 2} onClick={review_selection}></Main_Plain>
                    </ConsolidateBrandsCard>
                ) : null}
                {preview && !preview.resume ? <Grey_ text={props.t('Change selected brands')} onClick={change_selection}></Grey_> : null}
                {loadingPreview ? <Partial_Loader text={props.t('Preparing consolidation preview')}></Partial_Loader> : null}
                {error ? (
                    <ConsolidateBrandsCard role="alert">
                        <p>{props.t(error)}</p>
                        {!pendingChecked && !checkingPending ? <Grey_ text={props.t('Retry')} onClick={() => setPendingRetry(value => value + 1)}></Grey_> : null}
                        {pendingChecked && selectedIds.length >= 2 && !preview && !loadingPreview ? <Grey_ text={props.t('Retry preview')} onClick={review_selection}></Grey_> : null}
                    </ConsolidateBrandsCard>
                ) : null}
                {preview?.resume ? <p>{props.t('An unfinished brand consolidation is ready to resume. The reviewed brands and field choices are preserved.')}</p> : null}
                {preview ? (
                    <Merge_Preview
                        preview={preview}
                        choices={choices}
                        t={props.t}
                        disabled={saving || preview.resume}
                        onChoice={(key, value) => setChoices(current => ({ ...current, [key]: value }))}
                        onSurvivorChange={id => {
                            if (id !== preview.survivor.id) load_preview(id, [preview.survivor, ...previewSources].filter(record => record.id !== id).map(record => record.id));
                        }}
                    ></Merge_Preview>
                ) : null}
                {unresolved ? <p>{props.t('Choose a value for every conflicting field before consolidating the brands.')}</p> : null}
                {saving ? <Partial_Loader text={props.t('Consolidating brands')}></Partial_Loader> : null}
                {confirming ? (
                    <Confirmation_
                        message={props.t('Consolidate {{count}} brands into {{survivor}} and remove the others from active lists?', { count: previewSources.length + 1, survivor: preview.survivor.label })}
                        yes={props.t('Consolidate brands')}
                        no={props.t('Go back')}
                        yesDisabled={saving}
                        onYes={submit_consolidation}
                        onNo={() => { if (!savingRef.current) setConfirming(false); }}
                        onClose={() => { if (!savingRef.current) setConfirming(false); }}
                    ></Confirmation_>
                ) : null}
            </ConsolidateBrandsContent>
        </Slider_>
    );
}
